import { DecorationAssetModel } from "../specsModel/decorations/DecorationAssetModel";
import { DecorationCreditsModel } from "../specsModel/decorations/DecorationCreditsModel";
import { DecorationCreditsStepModel } from "../specsModel/decorations/DecorationCreditsStepModel";
import { DecorationDesignTemplateModel } from "../specsModel/decorations/DecorationDesignTemplateModel";
import { DecorationDisplayModel } from "../specsModel/decorations/DecorationDisplayModel";
import { DecorationGeneratorModel } from "../specsModel/decorations/DecorationGeneratorModel";
import { DecorationTechpackModel } from "../specsModel/decorations/DecorationTechpackModel";
import { LocalizedTextModel } from "../specsModel/decorations/LocalizedTextModel";
import { SpecsFabricColorCompatibilityModel } from "../specsModel/fabrics/SpecsFabricColorCompatibilityModel";
import { SpecsFabricColorModel } from "../specsModel/fabrics/SpecsFabricColorModel";
import { SpecsFamilyProductModel } from "../specsModel/families/SpecsFamilyProductModel";
import { SpecsFamilyVersionModel } from "../specsModel/families/SpecsFamilyVersionModel";
import type { SpecsEntityType } from "../specsModel/specsLib";
import type { SpecsCatalogProduct } from "../specsModel/types";
import type {
  SpecsFormEntry,
  SpecsFormFieldSchema,
  SpecsFormOption,
  SpecsFormPageSchema,
} from "./types";

function field(
  path: string,
  overrides: Omit<SpecsFormFieldSchema, "kind" | "path"> = {}
): SpecsFormFieldSchema {
  return {
    kind: "field",
    path,
    ...overrides,
  };
}

function buildProductNameOptions(
  products: SpecsCatalogProduct[] = []
): SpecsFormOption[] {
  return products
    .map((product) => product.name)
    .filter((name): name is string => typeof name === "string" && !!name)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ label: name, value: name }));
}

function createFamilySchema(
  products: SpecsCatalogProduct[] = []
): SpecsFormPageSchema {
  const productNameOptions = buildProductNameOptions(products);
  const productNameField = productNameOptions.length
    ? field("name", {
        fieldType: "autocomplete",
        options: productNameOptions,
      })
    : field("name");

  return {
    sections: [
      {
        title: "Identity",
        description: "Core family metadata and pass-through root fields.",
        entries: [
          field("family.name"),
          field("family.defaultVersion"),
          field("family.extra"),
        ],
      },
      {
        title: "Versions",
        description: "Family versions and their linked products.",
        entries: [
          {
            kind: "array",
            path: "family.versions",
            title: "Versions",
            createItem: () => new SpecsFamilyVersionModel(),
            getItemLabel: (item, index) => {
              const name =
                item && typeof item === "object" && "name" in item
                  ? String((item as { name?: string }).name ?? "")
                  : "";
              return name || `Version ${index + 1}`;
            },
            entries: [
              field("name"),
              field("canCombineProductsToReachMinQty"),
              {
                kind: "array",
                path: "products",
                title: "Products",
                createItem: () => new SpecsFamilyProductModel(),
                getItemLabel: (item, index) => {
                  const name =
                    item && typeof item === "object" && "name" in item
                      ? String((item as { name?: string }).name ?? "")
                      : "";
                  return name || `Product ${index + 1}`;
                },
                entries: [productNameField, field("version"), field("extra")],
              },
              field("extra"),
            ],
          },
        ],
      },
    ],
  };
}

const fabricColorEntries: SpecsFormEntry[] = [
  field("name"),
  field("hex"),
  field("generatorHex"),
  field("label"),
  field("fallback"),
  field("textWhite"),
  field("techpackColorName"),
  field("chartName"),
  field("column"),
  field("textureUrl", { colSpan: 2 }),
  field("disabled"),
  field("colorEndOfLife"),
  field("migrateTo"),
  field("colorSkuId"),
  field("descriptionOverwrite", { colSpan: 2 }),
  {
    kind: "object",
    path: "colorCompatibility",
    title: "Color compatibility",
    createValue: () => new SpecsFabricColorCompatibilityModel(),
    columns: 2,
    colSpan: 2,
    entries: [
      field("colorTendsToBleed"),
      field("colorTendsToBeBledOn"),
      field("extra"),
    ],
  },
  field("extra", { colSpan: 4 }),
];

const fabricSchema: SpecsFormPageSchema = {
  sections: [
    {
      title: "Identity",
      description: "Core fabric metadata.",
      columns: 3,
      entries: [
        field("fabric.name"),
        field("fabric.group"),
        field("fabric.factory"),
        field("fabric.description", { colSpan: 2 }),
        field("fabric.fabricLot"),
        field("fabric.textColor"),
        field("fabric.extra", { colSpan: 3 }),
      ],
    },
    {
      title: "Default Color",
      description: "Optional default color used as the primary color surface.",
      entries: [
        {
          kind: "object",
          path: "fabric.defaultColor",
          title: "Default color",
          createValue: () => new SpecsFabricColorModel(),
          columns: 4,
          colSpan: 1,
          entries: [...fabricColorEntries],
        },
      ],
    },
    {
      title: "Colors",
      description: "Available fabric colors.",
      entries: [
        {
          kind: "array",
          path: "fabric.colors",
          title: "Colors",
          createItem: () => new SpecsFabricColorModel(),
          columns: 4,
          getItemLabel: (item, index) => {
            const name =
              item && typeof item === "object" && "name" in item
                ? String((item as { name?: string }).name ?? "")
                : "";
            return name || `Color ${index + 1}`;
          },
          entries: [...fabricColorEntries],
        },
      ],
    },
  ],
};

const decorationSchema: SpecsFormPageSchema = {
  sections: [
    {
      title: "Identity",
      description: "Core decoration metadata.",
      columns: 4,
      entries: [
        field("decoration.id"),
        field("decoration.name"),
        field("decoration.factory"),
        field("decoration.group"),
        field("decoration.behavesLike"),
        field("decoration.assetProcessing"),
        field("decoration.fileTypes", { colSpan: 2 }),
        field("decoration.legacyAssetTypes", { colSpan: 2 }),
        field("decoration.adminOnly"),
        field("decoration.distributorOnly"),
        field("decoration.defaultErrorMessageOverwrite", { colSpan: 2 }),
        field("decoration.extra", { colSpan: 4 }),
      ],
    },
    {
      title: "Display",
      description: "Localized labels and list display settings.",
      entries: [
        {
          kind: "object",
          path: "decoration.displayName",
          title: "Display name",
          createValue: () => new LocalizedTextModel("decoration displayName"),
          entries: [field("fr"), field("en"), field("extra")],
        },
        {
          kind: "object",
          path: "decoration.display",
          title: "Display settings",
          createValue: () => new DecorationDisplayModel(),
          entries: [
            field("listOrder"),
            field("colorImgUrl"),
            field("hideSizeValue"),
            field("extra"),
          ],
        },
      ],
    },
    {
      title: "Asset",
      description: "Asset rendering and generator-related fabric settings.",
      entries: [
        {
          kind: "object",
          path: "decoration.asset",
          title: "Asset",
          createValue: () => new DecorationAssetModel(),
          entries: [
            field("previewSize"),
            field("effect"),
            field("numberOfColors"),
            field("fabricName"),
            field("effectDetails"),
            field("extra"),
          ],
        },
        {
          kind: "object",
          path: "decoration.generator",
          title: "Generator",
          createValue: () => new DecorationGeneratorModel(),
          entries: [
            field("effect"),
            field("fabricName"),
            field("stitchFabricName"),
            field("extra"),
          ],
        },
      ],
    },
    {
      title: "Credits",
      description: "Credits and pricing step rules.",
      entries: [
        {
          kind: "object",
          path: "decoration.credits",
          title: "Credits",
          createValue: () => new DecorationCreditsModel(),
          entries: [
            field("initialCredits"),
            field("priceExplanationKey"),
            {
              kind: "array",
              path: "extraCreditsBasedOnSideLength",
              title: "Extra credits by side length",
              createItem: () => new DecorationCreditsStepModel(),
              getItemLabel: (_item, index) => `Step ${index + 1}`,
              entries: [
                field("thresholdInInches"),
                field("extraCredits"),
                field("priceExplanationKey"),
                field("extra"),
              ],
            },
            field("extra"),
          ],
        },
      ],
    },
    {
      title: "Techpack",
      description: "Techpack naming, sizing, and overwrite settings.",
      entries: [
        {
          kind: "object",
          path: "decoration.techpack",
          title: "Techpack",
          createValue: () => new DecorationTechpackModel(),
          entries: [
            field("decorationTypePrefix"),
            field("decorationNameKey"),
            field("putSizeInFilename"),
            field("addBordersToAsset"),
            field("displayAlignmentMarks"),
            field("widthInches"),
            field("heightInches"),
            field("borderType"),
            field("overwriteFabric"),
            field("extra"),
          ],
        },
      ],
    },
    {
      title: "Templates",
      description: "Design templates and remaining object blobs.",
      entries: [
        {
          kind: "array",
          path: "decoration.designTemplates",
          title: "Design templates",
          createItem: () => new DecorationDesignTemplateModel(),
          getItemLabel: (item, index) => {
            const name =
              item && typeof item === "object" && "name" in item
                ? String((item as { name?: string }).name ?? "")
                : "";
            return name || `Template ${index + 1}`;
          },
          entries: [field("name"), field("url"), field("extra")],
        },
        {
          kind: "field",
          path: "decoration.tagSizes",
          label: "Tag sizes",
          fieldType: "json",
          minRows: 8,
        },
        {
          kind: "field",
          path: "decoration.repeatingPattern",
          label: "Repeating pattern",
          fieldType: "json",
          minRows: 8,
        },
        {
          kind: "field",
          path: "decoration.factoryPricing",
          label: "Factory pricing",
          fieldType: "json",
          minRows: 8,
        },
        {
          kind: "field",
          path: "decoration.sizeLabel",
          label: "Size label",
          fieldType: "json",
          minRows: 8,
        },
      ],
    },
  ],
};

export function getSpecsAutoFormSchema(
  specType: SpecsEntityType,
  options: { products?: SpecsCatalogProduct[] } = {}
): SpecsFormPageSchema {
  if (specType === "family") {
    return createFamilySchema(options.products);
  }
  if (specType === "fabric") {
    return fabricSchema;
  }
  return decorationSchema;
}
